// Match → Meet → Keep lifecycle UI primitives (Feb 2026).
//
// The defining loop of Meet to Keep. This module exposes three surfaces that
// mount inside Chat.jsx:
//   • <LifecyclePill> — persistent header chip that mirrors the current stage
//     and opens the appropriate bottom sheet on tap.
//   • <LifecycleSheet> — bottom sheet with Meet / We Met / Keep CTAs, contextual
//     copy, and the Not-This-Time destructive option.
//   • <LifecycleCelebration> — takeover screen for mutual milestones.
//
// All three read from the `lifecycle` projection embedded in /api/chat/{id}
// and /api/matches (per-viewer private state). Server is authoritative on
// `can_*` flags; UI mirrors for micro-interactions only.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MapPin, CheckCircle2, Sparkles, X } from 'lucide-react';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import ConfettiBurst from '@/components/ConfettiBurst';
import { playCelebrationChime } from '@/lib/celebrationChime';

// -------- Stage → visual + copy map --------------------------------------
// Feb 26 2026 · Extended with `ready_to_plan`, `date_pending`, `planned`
// substates. The pill exposes different labels per stage so users see
// "Awaiting response" / "Date proposal" / "Planned" appropriately.
export const STAGE_META = {
  matched: {
    label: 'Matched', icon: Heart,
    color: '#B91C1C', bg: 'rgba(185,28,28,0.08)',
    cta: 'I want to Meet',
  },
  meet_pending: {
    label: 'Interested in meeting', icon: MapPin,
    color: '#B38138', bg: 'rgba(179,129,56,0.10)',
    cta: 'I want to Meet',
    hint: 'Waiting for them — we\'ll let you know if it\'s mutual.',
  },
  ready_to_plan: {
    label: 'Ready to plan', icon: MapPin,
    color: '#B38138', bg: 'rgba(179,129,56,0.10)',
    cta: 'Plan a date',
    hint: 'You both want to meet. Suggest a date, time and place.',
  },
  date_pending: {
    // Same visual as ready_to_plan; the pill overrides the label per-viewer
    // so proposer sees "Awaiting response" and recipient sees "Date proposal".
    label: 'Date proposal', icon: MapPin,
    color: '#B38138', bg: 'rgba(179,129,56,0.10)',
    cta: 'View proposal',
  },
  planned: {
    label: 'Planned', icon: MapPin,
    color: '#15803D', bg: 'rgba(21,128,61,0.10)',
    cta: 'View date',
    hint: 'You\'re both set. Tap "We Met" once the date happens.',
  },
  meeting: {
    // Legacy alias — kept so any stale client that still emits `meeting` (from
    // `lifecycle_stage_v1`) doesn't crash the pill lookup.
    label: 'Planning', icon: MapPin,
    color: '#B38138', bg: 'rgba(179,129,56,0.10)',
    cta: 'Plan a date',
  },
  met: {
    label: 'Met', icon: CheckCircle2,
    color: '#1E4FD8', bg: 'rgba(30,79,216,0.10)',
    cta: 'Keep',
    hint: 'Would you like to see them again? Your answer stays private.',
  },
  kept: {
    label: 'Kept', icon: Heart,
    color: '#B91C1C', bg: 'rgba(185,28,28,0.12)',
    cta: 'Plan another date',
    hint: 'You both want to keep seeing each other.',
  },
  archived: {
    label: 'Archived', icon: X,
    color: '#8A8A93', bg: 'rgba(0,0,0,0.05)',
    cta: null,
    hint: 'This connection has ended.',
  },
};


// Per-viewer pill label override. `date_pending` splits into two visual states:
// proposer sees "Awaiting response", recipient sees "Date proposal received".
function pillLabelFor(lifecycle) {
  const meta = STAGE_META[lifecycle.lifecycle_stage] || STAGE_META.matched;
  if (lifecycle.lifecycle_stage === 'date_pending') {
    return lifecycle.i_am_proposer ? 'Awaiting response' : 'Date proposal';
  }
  return meta.label;
}


// -------- LifecyclePill --------------------------------------------------
// Compact chip in the chat header. Muted when there's nothing to act on,
// coloured when the user has a pending action. Tap → opens LifecycleSheet.
export function LifecyclePill({ lifecycle, otherName, onOpen }) {
  if (!lifecycle) return null;
  const meta = STAGE_META[lifecycle.lifecycle_stage] || STAGE_META.matched;
  const Icon = meta.icon;
  const showActionable = lifecycle.can_meet || lifecycle.can_confirm_met
                       || lifecycle.can_keep || lifecycle.can_propose_date
                       || lifecycle.can_view_date
                       || lifecycle.lifecycle_stage === 'date_pending';
  return (
    <button
      onClick={onOpen}
      data-testid={`lifecycle-pill-${lifecycle.lifecycle_stage}`}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-tight transition-all
        ${showActionable ? 'shadow-sm hover:shadow-md' : 'opacity-80 hover:opacity-100'}`}
      style={{ backgroundColor: meta.bg, color: meta.color, border: `1px solid ${meta.color}22` }}
    >
      <Icon size={12} strokeWidth={2.2} />
      <span>{pillLabelFor(lifecycle)}</span>
      {lifecycle.keep_cycle > 1 && (
        <span className="text-[9px] opacity-70">· ×{lifecycle.keep_cycle}</span>
      )}
    </button>
  );
}


// -------- LifecycleSheet -------------------------------------------------
// Bottom sheet with contextual CTA. Delegates the actual server tap to the
// caller (Chat.jsx) so the celebration + local state update flow through one
// path. Feb 26 2026 · Extended with plan_date / view_date / accept / decline /
// counter / cancel_plan actions to drive the Planned milestone.
export function LifecycleSheet({ open, onClose, lifecycle, otherName, onAction }) {
  if (!open || !lifecycle) return null;
  const stage = lifecycle.lifecycle_stage;
  const meta = STAGE_META[stage] || STAGE_META.matched;
  const proposal = lifecycle.current_proposal;

  // Primary action derivation per stage/role.
  const actionFor = () => {
    // Recipient of a pending proposal — offer Accept / Decline / Counter.
    if (stage === 'date_pending' && !lifecycle.i_am_proposer && proposal) {
      return {
        key: 'accept', label: `Accept this date`,
        copy: `${otherName || 'They'} proposed ${_fmtDateTime(proposal.date_at)} at ${proposal.location}. Accepting confirms Planned for both of you.`,
        secondary: [
          { key: 'counter', label: 'Suggest a different date' },
          { key: 'decline', label: 'Decline', destructive: true },
        ],
      };
    }
    // Proposer awaiting response — offer Cancel proposal.
    if (stage === 'date_pending' && lifecycle.i_am_proposer && proposal) {
      return {
        key: 'noop', label: null,
        copy: `Waiting on ${otherName || 'them'} to respond to ${_fmtDateTime(proposal.date_at)} at ${proposal.location}.`,
        secondary: [
          { key: 'cancel_proposal', label: 'Withdraw proposal', destructive: true },
        ],
      };
    }
    // Confirmed date — offer View Date / Cancel Plan.
    if (lifecycle.can_view_date && stage === 'planned') {
      return {
        key: 'view_date', label: 'View date details',
        copy: proposal
          ? `${_fmtDateTime(proposal.date_at)} at ${proposal.location}${proposal.notes ? ` — ${proposal.notes}` : ''}.`
          : `You've both confirmed a date. Tap "We Met" once it's happened.`,
        secondary: [
          { key: 'we_met', label: `Yes, we met` },
          { key: 'cancel_plan', label: 'Cancel this date', destructive: true },
        ],
      };
    }
    // Ready to plan — offer Plan Date.
    if (lifecycle.can_propose_date) {
      return {
        key: 'plan_date', label: 'Plan a date',
        copy: `You both want to meet. Suggest a date, time and place — ${otherName || 'they'}'ll be able to accept, decline or counter.`,
      };
    }
    // Standard existing actions.
    if (lifecycle.can_meet)         return { key: 'meet',   label: 'I want to Meet',
                                             copy: `Privately record that you'd like to meet ${otherName || 'them'}. They won't know unless they also want to meet.` };
    if (lifecycle.can_confirm_met)  return { key: 'we_met', label: 'We Met',
                                             copy: `Confirm the date happened. Once you both confirm, you'll get the Keep decision.` };
    if (lifecycle.can_keep)         return { key: 'keep',   label: 'Keep seeing them',
                                             copy: `You'd like another date. Your answer stays private unless they choose Keep too. Keep doesn't mean exclusivity — just that you'd like to see them again.` };
    return null;
  };
  const primary = actionFor();
  const canAct = !!primary && primary.key !== 'noop';

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
         onClick={onClose} data-testid="lifecycle-sheet">
      <div onClick={e => e.stopPropagation()}
           className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 pb-8 shadow-2xl">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
               style={{ backgroundColor: meta.bg }}>
            <meta.icon size={20} strokeWidth={2} style={{ color: meta.color }} />
          </div>
          <div className="flex-1">
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: meta.color }}>
              {pillLabelFor(lifecycle)}
            </p>
            <h3 className="font-brand text-[20px] font-[500] tracking-tight text-[#0F0F10] mt-0.5">
              {stage === 'matched' && `Ready to meet ${otherName}?`}
              {stage === 'meet_pending' && `You've tapped Meet`}
              {stage === 'ready_to_plan' && `Plan a date with ${otherName}`}
              {stage === 'date_pending' && (lifecycle.i_am_proposer
                ? `Waiting on ${otherName}`
                : `${otherName} proposed a date`)}
              {stage === 'planned' && `You're both set with ${otherName}`}
              {stage === 'meeting' && `Have you met ${otherName}?`}
              {stage === 'met' && `Keep seeing ${otherName}?`}
              {stage === 'kept' && `You're Keeping each other`}
              {stage === 'archived' && `This connection has ended`}
            </h3>
          </div>
          <button onClick={onClose} data-testid="lifecycle-sheet-close"
                  className="p-1 rounded-full hover:bg-black/5">
            <X size={18} strokeWidth={2} className="text-[#4B4B52]" />
          </button>
        </div>

        {primary && (
          <p className="text-[13px] leading-relaxed text-[#4B4B52] mb-5">{primary.copy}</p>
        )}
        {!primary && meta.hint && (
          <p className="text-[13px] leading-relaxed text-[#4B4B52] mb-5">{meta.hint}</p>
        )}

        {canAct && primary.label && (
          <button
            onClick={() => onAction(primary.key)}
            data-testid={`lifecycle-primary-${primary.key}`}
            className="w-full py-3.5 rounded-full text-white font-semibold text-[14px] tracking-tight transition-all hover:opacity-90 mb-2"
            style={{ backgroundColor: meta.color }}
          >
            {primary.label}
          </button>
        )}
        {/* Keep's "Not this time" preserved */}
        {primary && primary.key === 'keep' && (
          <button
            onClick={() => onAction('not_this_time')}
            data-testid="lifecycle-not-this-time"
            className="w-full py-3.5 rounded-full bg-white border border-[var(--hairline-strong)] text-[#4B4B52] font-semibold text-[14px] tracking-tight transition-all hover:bg-black/[0.03]"
          >
            Not this time
          </button>
        )}
        {/* Secondary buttons (Counter/Decline/Cancel Plan/We Met on planned/etc). */}
        {primary && (primary.secondary || []).map((s) => (
          <button
            key={s.key}
            onClick={() => onAction(s.key)}
            data-testid={`lifecycle-secondary-${s.key}`}
            className={`w-full py-3.5 rounded-full text-[14px] font-semibold tracking-tight transition-all mt-2
              ${s.destructive
                ? 'bg-white border border-[var(--hairline-strong)] text-[#B91C1C] hover:bg-[#B91C1C]/[0.05]'
                : 'bg-white border border-[var(--hairline-strong)] text-[#4B4B52] hover:bg-black/[0.03]'}`}
          >
            {s.label}
          </button>
        ))}
        {lifecycle.mutual_meet_at && stage === 'ready_to_plan' && (
          <p className="text-[11px] text-[#8A8A93] mt-4 text-center">
            📍 Mutual Meet since {new Date(lifecycle.mutual_meet_at).toLocaleDateString()}
          </p>
        )}
        {lifecycle.mutual_date_planned_at && stage === 'planned' && (
          <p className="text-[11px] text-[#8A8A93] mt-4 text-center">
            🎉 Planned since {new Date(lifecycle.mutual_date_planned_at).toLocaleDateString()}
          </p>
        )}
        {lifecycle.mutual_met_at && stage === 'met' && (
          <p className="text-[11px] text-[#8A8A93] mt-4 text-center">
            ✓ Both confirmed the date on {new Date(lifecycle.mutual_met_at).toLocaleDateString()}
          </p>
        )}
      </div>
    </div>
  );
}


// Local date-time formatter for the sheet.
function _fmtDateTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined,
      { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
}


// -------- LifecycleCelebration ------------------------------------------
// Full-screen takeover fired on mutual milestones (meet / met / keep). Auto-
// dismisses after 4.5s; user can tap to dismiss earlier.
//
// Feb 26 2026 · Dual-avatar mode. When the backend supplies a `celebration`
// payload ({viewer: {photo_url,display_name}, other_user: {...}}), render two
// overlapping circular avatars instead of the generic milestone icon. Falls
// back to the icon-only layout if `celebration` is missing (older clients or
// non-mutual events).
export function LifecycleCelebration({ milestone, otherName, celebration, onDismiss }) {
  useEffect(() => {
    if (!milestone) return undefined;
    // Play the celebration chime the instant the takeover mounts. Reduced-motion
    // users are respected inside the util.
    playCelebrationChime(milestone);
    const t = setTimeout(() => onDismiss(), 4500);
    return () => clearTimeout(t);
  }, [milestone, onDismiss]);
  if (!milestone) return null;

  const config = {
    meet: {
      big: 'IT\u2019S A MEET!', line: `You and ${otherName || 'them'} both want to meet.`,
      sub: 'Time to make it real.', icon: MapPin, color: '#15803D',
    },
    planned: {
      big: 'IT\u2019S PLANNED!', line: `You and ${otherName || 'them'} agreed on the date.`,
      sub: 'Set the reminder — it\u2019s on.', icon: MapPin, color: '#15803D',
    },
    met: {
      big: 'DATE CONFIRMED', line: `You and ${otherName || 'them'} both confirmed you met.`,
      sub: 'How was it?', icon: CheckCircle2, color: '#1E4FD8',
    },
    keep: {
      big: 'IT\u2019S A KEEP!', line: `You and ${otherName || 'them'} both want to keep seeing each other.`,
      sub: 'Looks like this one\u2019s worth keeping.', icon: Heart, color: '#B91C1C',
    },
  }[milestone];
  if (!config) return null;
  const Icon = config.icon;
  const viewer = celebration?.viewer;
  const other  = celebration?.other_user;

  return (
    <button
      onClick={onDismiss}
      data-testid={`lifecycle-celebration-${milestone}`}
      className="fixed inset-0 z-[120] flex flex-col items-center justify-center bg-white text-left animate-in fade-in duration-300"
    >
      {(viewer && other) ? (
        // Dual-avatar layout — two overlapping circles with a small milestone
        // icon badge sitting in the seam so the emotional beat still reads.
        // Confetti bursts outward from behind the avatars (Feb 26 2026).
        <div className="relative flex items-center justify-center mb-6"
             style={{ minWidth: 200, minHeight: 112 }}>
          <ConfettiBurst
            count={44}
            colors={[config.color, '#F59E0B', '#EC4899', '#1E4FD8', '#15803D']}
            spread={260}
            duration={1800}
            data-testid={`celebration-confetti-${milestone}`}
          />
          <DualAvatar photoUrl={viewer.photo_url} name={viewer.display_name}
                      ringColor={config.color} className="animate-in zoom-in duration-500"
                      style={{ marginRight: -22, zIndex: 2 }} />
          <DualAvatar photoUrl={other.photo_url} name={other.display_name}
                      ringColor={config.color} className="animate-in zoom-in duration-500"
                      style={{ marginLeft: -22, zIndex: 1, animationDelay: '120ms' }} />
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-9 h-9 rounded-full flex items-center justify-center shadow-md z-10"
               style={{ backgroundColor: config.color }}>
            <Icon size={18} strokeWidth={2.2} color="white" />
          </div>
        </div>
      ) : (
        <div className="relative w-24 h-24 flex items-center justify-center mb-6">
          <ConfettiBurst count={30}
                         colors={[config.color, '#F59E0B', '#EC4899']}
                         spread={200} duration={1600} />
          <div className="w-24 h-24 rounded-full flex items-center justify-center animate-in zoom-in duration-500 relative z-10"
               style={{ backgroundColor: `${config.color}18` }}>
            <Icon size={44} strokeWidth={1.8} style={{ color: config.color }} />
          </div>
        </div>
      )}
      <p className="text-[11px] font-bold uppercase tracking-[0.3em] mb-3" style={{ color: config.color }}>
        {milestone === 'keep' ? '❤️ Match to Keep' : '🎉 Milestone'}
      </p>
      <h1 className="font-brand text-[36px] sm:text-[48px] font-[500] tracking-tight text-[#0F0F10] text-center px-6 mb-4">
        {config.big}
      </h1>
      <p className="text-[15px] text-[#0F0F10] font-semibold text-center px-6 max-w-md">
        {config.line}
      </p>
      <p className="text-[13px] text-[#4B4B52] text-center px-6 mt-2 max-w-md">
        {config.sub}
      </p>
      <p className="text-[10px] text-[#8A8A93] mt-10 uppercase tracking-wider">Tap anywhere to continue</p>
    </button>
  );
}


// Small internal — overlapping avatar circle with white ring + soft shadow.
function DualAvatar({ photoUrl, name, ringColor, style, className }) {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  return (
    <div
      className={`w-28 h-28 rounded-full bg-white flex items-center justify-center shadow-lg ${className || ''}`}
      style={{ boxShadow: '0 8px 24px rgba(15,15,16,0.18)', border: `4px solid #fff`, outline: `2px solid ${ringColor}22`, ...style }}
    >
      {photoUrl ? (
        <img src={photoUrl} alt={name || ''} className="w-full h-full rounded-full object-cover" data-testid="celebration-avatar-img" />
      ) : (
        <div className="w-full h-full rounded-full flex items-center justify-center text-2xl font-semibold text-white"
             style={{ backgroundColor: ringColor }} data-testid="celebration-avatar-initial">
          {initial}
        </div>
      )}
    </div>
  );
}


// -------- LifecycleController -------------------------------------------
// Convenience hook that composes the three pieces + the server dispatch. Chat
// mounts this and only needs to hand it (matchId, lifecycle, otherName,
// onLifecycleUpdate) — the controller returns the pill + mounts the sheet +
// celebration itself and handles all the network calls.
export function useLifecycleController({ matchId, lifecycle, otherName, onLifecycleUpdate, onOpenDatePlanner, onOpenProposalComposer, onOpenViewDate }) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [celebration, setCelebration] = useState(null);
  const [celebrationData, setCelebrationData] = useState(null);  // dual-avatar payload
  const [pendingHint, setPendingHint] = useState(null);   // fires after celebration dismiss
  const navigate = useNavigate();

  const dispatch = async (actionKey) => {
    try {
      // Feb 26 2026 · Non-network actions — hand off to Chat.jsx for local UI.
      if (actionKey === 'plan_date') {
        setSheetOpen(false);
        if (onOpenProposalComposer) onOpenProposalComposer(null);
        return;
      }
      if (actionKey === 'counter') {
        setSheetOpen(false);
        // Prefill composer with the current proposal so recipient can tweak.
        if (onOpenProposalComposer) onOpenProposalComposer(lifecycle?.current_proposal || null, 'counter');
        return;
      }
      if (actionKey === 'view_date') {
        setSheetOpen(false);
        if (onOpenViewDate) onOpenViewDate(lifecycle?.current_proposal || null);
        return;
      }
      let r;
      if (actionKey === 'meet') {
        r = await api.post(`/matches/${matchId}/meet`);
      } else if (actionKey === 'we_met') {
        r = await api.post(`/matches/${matchId}/we-met`);
      } else if (actionKey === 'keep') {
        r = await api.post(`/matches/${matchId}/keep`, { decision: 'keep' });
      } else if (actionKey === 'not_this_time') {
        r = await api.post(`/matches/${matchId}/keep`, { decision: 'not_this_time' });
      } else if (actionKey === 'accept') {
        const pid = lifecycle?.current_proposal?.proposal_id;
        if (!pid) return;
        r = await api.post(`/date-proposals/${pid}/accept`);
      } else if (actionKey === 'decline') {
        const pid = lifecycle?.current_proposal?.proposal_id;
        if (!pid) return;
        r = await api.post(`/date-proposals/${pid}/decline`, {});
      } else if (actionKey === 'cancel_proposal' || actionKey === 'cancel_plan') {
        const pid = lifecycle?.current_proposal?.proposal_id;
        if (!pid) return;
        r = await api.post(`/date-proposals/${pid}/cancel`, {});
      } else {
        return;
      }
      setSheetOpen(false);
      if (r.data.state) onLifecycleUpdate(r.data.state);
      if (r.data.hint) setPendingHint(r.data.hint);
      // Fire the appropriate celebration.
      let milestone = null;
      if (r.data.mutual_now) {
        milestone = actionKey === 'meet' ? 'meet'
                  : actionKey === 'we_met' ? 'met'
                  : actionKey === 'keep' ? 'keep'
                  : actionKey === 'accept' ? 'planned' : null;
      }
      if (milestone) {
        setCelebration(milestone);
        setCelebrationData(r.data.celebration || null);
      } else if (r.data.declined) {
        toast.success('Your decision stays private. This match has ended.', { duration: 3500 });
      } else if (actionKey === 'decline') {
        toast.success('Proposal declined. They\'ll be notified.', { duration: 3000 });
      } else if (actionKey === 'cancel_proposal') {
        toast.success('Proposal withdrawn.', { duration: 3000 });
      } else if (actionKey === 'cancel_plan') {
        toast.success('Date cancelled. You can plan another when ready.', { duration: 4000 });
      } else if (!r.data.already_recorded) {
        toast.success(
          actionKey === 'meet' ? '✓ Recorded — we\'ll let you know if it\'s mutual'
          : actionKey === 'we_met' ? '✓ We Met recorded'
          : '❤️ Keep recorded — waiting for them',
          { duration: 3000 }
        );
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Something went wrong');
      toast.error(msg);
    }
  };

  const onCelebrationDismiss = () => {
    setCelebration(null);
    setCelebrationData(null);
    // Feb 2026 · Post-milestone handoff. On mutual Meet the backend attaches a
    // hint pointing at the AI Date Planner with the "first date ideas" preset,
    // so couples don't stall on "now what?". Route after a short beat so the
    // celebration fade-out completes cleanly.
    if (pendingHint?.type === 'open_date_planner' && (pendingHint.route || pendingHint.preset)) {
      const preset = pendingHint.preset || 'ideas_for_first_date';
      const route = pendingHint.route;
      const cta = pendingHint.cta || 'See ideas';
      setPendingHint(null);
      setTimeout(() => {
        toast(cta, {
          duration: 6000,
          action: {
            label: 'Open',
            onClick: () => {
              // Web: open the Date Planner in-place with the right preset.
              // Mobile / callers without a local Planner: fall back to route nav.
              if (onOpenDatePlanner) {
                onOpenDatePlanner(preset);
              } else if (route) {
                navigate(route);
              }
            },
          },
        });
      }, 300);
    } else {
      setPendingHint(null);
    }
  };

  return {
    pill: (
      <LifecyclePill lifecycle={lifecycle} otherName={otherName}
                     onOpen={() => setSheetOpen(true)} />
    ),
    sheet: (
      <LifecycleSheet open={sheetOpen} onClose={() => setSheetOpen(false)}
                      lifecycle={lifecycle} otherName={otherName}
                      onAction={dispatch} />
    ),
    celebration: (
      <LifecycleCelebration milestone={celebration} otherName={otherName}
                            celebration={celebrationData}
                            onDismiss={onCelebrationDismiss} />
    ),
    openSheet: () => setSheetOpen(true),
  };
}
